import React, { useMemo, useRef } from 'react';
import { FixedSizeList as List } from 'react-window';
import { useAppSelector } from '../../../../redux/hooks';
import { RootState } from 'redux/store';
import { Row, Col, Button, Tooltip } from 'antd';
import { ConferenceIcon, ExtensionIconNew, FaxServerIcon, FlowIcon, IvrMenuIcon, PagingGroupIcon, QueueIcon, RingGroupIcon, TransferIcon } from 'assets';

export interface TransferModalExtensionsListProps {
    onTransfer?: (ext: string) => void;
    excludeExts?: string[];
    itemHeight?: number;
    searchTerm?: string;
}

interface ItemData {
    extensions: Array<{ id: string; name: string; ext: string; appName: string; status?: string }>
    onTransfer?: (ext: string) => void;
}

const extensionIconMap = {
    'conference': ConferenceIcon,
    'extension': ExtensionIconNew,
    'fax_server': FaxServerIcon,
    'flow': FlowIcon,
    'ivr_menu': IvrMenuIcon,
    'paging_group': PagingGroupIcon,
    'queue': QueueIcon,
    'ring_group': RingGroupIcon,
}

const defaultItemHeight = 80;

const humanize = (key: string): string => key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const TransferModalExtensionsList: React.FC<TransferModalExtensionsListProps> = ({ onTransfer, excludeExts, itemHeight, searchTerm }) => {
    const apps = useAppSelector((state: RootState) => state?.apps?.data);

    const prevItemsRef = useRef<Array<{ id: string; name: string; ext: string; appName: string }>>([]);
    const stableItemsRef = useRef<Array<{ id: string; name: string; ext: string; appName: string }>>([]);

    const stableAllExtensions = useMemo(() => {
        const keys = [
            'conference',
            'extension',
            'fax_server',
            'flow',
            'ivr_menu',
            'paging_group',
            'queue',
            'ring_group',
        ] as const;

        const nextItems: Array<{ id: string; name: string; ext: string; appName: string; status?: string }> = [];
        keys.forEach((k) => {
            const list = (apps)?.[k] || [];
            list.forEach((it) => {
                nextItems.push({ id: (it as any)?.id || '', name: (it as any)?.name || '', ext: (it as any)?.extension || '', appName: k, status: k === 'extension' ? (it as any)?.status : undefined });
            });
        });

        const prev = prevItemsRef.current as Array<{ id: string; name: string; ext: string; appName: string; status?: string }>;
        const hasChanged = nextItems.length !== prev.length || nextItems.some((item, idx) => {
            const p = prev[idx];
            return !p || p.id !== item.id || p.name !== item.name || p.ext !== item.ext || p.appName !== item.appName || p.status !== item.status;
        });

        if (hasChanged) {
            prevItemsRef.current = nextItems;
            stableItemsRef.current = nextItems;
        }

        return stableItemsRef.current;
    }, [apps]);

    const allExtensions = useMemo(() => {
        const withoutExcluded = excludeExts?.length ? stableAllExtensions.filter(i => i.ext && !excludeExts.includes(i.ext)) : stableAllExtensions;
        if (!searchTerm) return withoutExcluded;
        const q = String(searchTerm).toLowerCase();
        return withoutExcluded.filter(i => i.name.toLowerCase().includes(q) || i.ext.toLowerCase().includes(q) || i.appName.toLowerCase().includes(q));
    }, [stableAllExtensions, excludeExts, searchTerm]);

    const itemData: ItemData = useMemo(() => ({ extensions: allExtensions, onTransfer }), [allExtensions, onTransfer]);

    return (
        <List
            height={480}
            width="100%"
            itemCount={allExtensions.length}
            itemSize={itemHeight || defaultItemHeight}
            itemData={itemData}
            className="extension-list-container"
            overscanCount={5}
        >
            {TransferItem}
        </List>
    );
};

 const TransferItem: React.FC<{ index: number; style: React.CSSProperties; data: ItemData }> = ({ index, style, data }) => {
     const item = data.extensions[index];
     if (!item) return null;

     const status = item.status || "Available";
     const lowerStatus = status.toLowerCase();

     let statusClassName;

     switch (lowerStatus) {
         case "available":
             statusClassName = "available";
             break;
         case "offline":
             statusClassName = "offline";
             break;
         case "in call":
             statusClassName = "oncall";
             break;
         case "ringing":
             statusClassName = "ringing";
             break;
         default:
             statusClassName = "offline";
             break;
     }

     return (
         <div
             onClick={() => {
                 data.onTransfer?.(item.ext);
             }}
             style={style}
         >
             <div className='transfer-extension-item'>
                 <Row justify="space-between" align="middle">
                     <Col>
                         <Row gutter={15} align="middle">
                             <Tooltip title={humanize(item?.appName)}>
                                 <img className='extension-icon' src={extensionIconMap[item?.appName as keyof typeof extensionIconMap]} />
                             </Tooltip>
                             <Col>
                                 <Row gutter={10}>
                                     <Col>
                                         <p title={item.name} className='ext-heading truncate-text'>{item.name}</p>
                                     </Col>
                                     {item?.appName === 'extension' && status && (
                                         <Col>
                                             <Row gutter={5} align={"middle"} className={statusClassName}>
                                                 <Col>
                                                     <span className='ext-dot' />
                                                 </Col>
                                                 <Col>
                                                     <p className='ext-status-text'>{item.status || "Available"}</p>
                                                 </Col>
                                             </Row>
                                         </Col>
                                     )}
                                 </Row>
                                 <p className='ext-num'>{item.ext}</p>
                             </Col>
                         </Row>
                     </Col>
                     <Col>
                         <Row align={"middle"} gutter={20}>
                             <Button icon={<img src={TransferIcon} className='transfer-button-icon' />} iconPosition='start' className='transfer-button' />
                         </Row>
                     </Col>
                 </Row>
             </div>
         </div>
     );
 };

export default React.memo(TransferModalExtensionsList);


